import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/services', label: 'Services' },
    { path: '/a-propos', label: 'À propos' },
    { path: '/zone-intervention', label: 'Zone d\'intervention' },
    { path: '/realisations', label: 'Réalisations' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#1997b0] text-white py-2 hidden md:block">
        <div className="container-custom">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <a 
                href="tel:0603054911" 
                className="flex items-center gap-2 hover:text-[#fabc63] transition-colors"
                data-testid="header-phone"
              >
                <Phone size={14} />
                <span>06 03 05 49 11</span>
              </a>
              <a 
                href="mailto:admconfortclim@gmail.com" 
                className="flex items-center gap-2 hover:text-[#fabc63] transition-colors"
                data-testid="header-email"
              >
                <Mail size={14} />
                <span>admconfortclim@gmail.com</span>
              </a>
            </div>
            <div className="flex items-center gap-2 text-[#fabc63] font-medium">
              <Clock size={14} />
              <span>Dépannage urgent 24h/24 - 7j/7</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-md' 
            : 'bg-white'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20 md:h-[88px] lg:h-24">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center shrink-0"
              data-testid="header-logo"
            >
              <img 
                src="/assets/logo-adm-confort-clim.png"
                alt="ADM Confort Clim - Climatisation et Pompe à chaleur"
                className="h-12 sm:h-14 md:h-16 lg:h-[72px] xl:h-20 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 xl:px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    location.pathname === link.path
                      ? 'bg-[#1997b0]/10 text-[#1997b0]'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:0603054911"
                className="btn-emergency flex items-center gap-2 text-sm !px-5 !py-2.5 whitespace-nowrap"
                data-testid="header-cta-call"
              >
                <Phone size={16} />
                <span>Urgence</span>
              </a>
              <Link
                to="/contact"
                className="btn-primary flex items-center gap-2 text-sm !px-6 !py-2.5 whitespace-nowrap"
                data-testid="header-cta-quote"
              >
                Devis gratuit
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-slate-100 overflow-hidden"
            >
              <nav className="container-custom py-4 flex flex-col gap-1" data-testid="mobile-nav">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 ${
                      location.pathname === link.path
                        ? 'bg-[#1997b0]/10 text-[#1997b0]'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-slate-100">
                  <a
                    href="tel:0603054911"
                    className="btn-emergency flex items-center justify-center gap-2"
                    data-testid="mobile-cta-call"
                  >
                    <Phone size={18} />
                    <span>Appel urgent : 06 03 05 49 11</span>
                  </a>
                  <Link
                    to="/contact"
                    className="btn-primary flex items-center justify-center gap-2"
                    data-testid="mobile-cta-quote"
                  >
                    Demander un devis gratuit
                  </Link>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
